import { FC, useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { appStore } from "@store/appStore";

const MIN_REASON_LENGTH = 15;

interface Props {
    show: boolean;
    targetName: string;
    onConfirm: (reason: string, proof: { password?: string; googleIdToken?: string }) => Promise<void>;
    onClose: () => void;
}

/**
 * Re-authentication before viewing as somebody else, by whichever method the
 * admin actually signs in with. Most of the remaining Super Admins use Google
 * Workspace and may never have set a platform password, so demanding one would
 * shut them out of the feature.
 *
 * The Google button mints a fresh id_token at the moment it is clicked; the
 * server rejects one older than five minutes, so this is a genuine step-up
 * rather than a replay of the morning's sign-in.
 */
const ImpersonationStartModal: FC<Props> = ({ show, targetName, onConfirm, onClose }) => {
    const [reason, setReason] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const googleButtonRef = useRef<HTMLDivElement | null>(null);

    const reasonOk = reason.trim().length >= MIN_REASON_LENGTH;

    const handleClose = () => {
        if (submitting) return;
        setReason("");
        setPassword("");
        setError(null);
        onClose();
    };

    const submit = async (proof: { password?: string; googleIdToken?: string }) => {
        setSubmitting(true);
        setError(null);
        try {
            await onConfirm(reason.trim(), proof);
            setReason("");
            setPassword("");
            onClose();
        } catch (e: any) {
            setError(e?.message || "Failed to start viewing as this user.");
        } finally {
            setSubmitting(false);
        }
    };

    // Render Google's button only once there is a reason, so the token is minted
    // after the operator has committed to the action rather than going stale
    // while they type.
    useEffect(() => {
        if (!show || !reasonOk) return;
        const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
        if (!clientId) return;

        let cancelled = false;
        const render = () => {
            if (cancelled) return;
            if (!window.google?.accounts?.id) {
                window.setTimeout(render, 100);
                return;
            }
            if (!googleButtonRef.current) return;

            window.google.accounts.id.initialize({
                client_id: clientId,
                hint: appStore.loggedInUser?.email,
                callback: (response: { credential: string }) =>
                    submit({ googleIdToken: response.credential }),
            });
            googleButtonRef.current.innerHTML = "";
            window.google.accounts.id.renderButton(googleButtonRef.current, {
                type: "standard",
                text: "continue_with",
                size: "large",
                theme: "outline",
                shape: "rectangular",
                width: 320,
            });
        };
        render();

        return () => {
            cancelled = true;
            window.google?.accounts?.id?.cancel?.();
        };
        // reason is read inside submit() at click time, so it does not belong here
         
    }, [show, reasonOk]);

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>View as {targetName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small mb-3">
                    You will see the platform exactly as <strong>{targetName}</strong> sees it
                    for 5 minutes. Everything you do — including what you read — is logged
                    against your account, not theirs.
                </p>
                <Form.Group>
                    <Form.Label>Reason</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        autoFocus
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Investigating stuck upload on ticket #1234"
                        disabled={submitting}
                    />
                    <Form.Text className={reasonOk ? "text-muted" : "text-danger"}>
                        At least {MIN_REASON_LENGTH} characters — this is what the audit
                        trail will show.
                    </Form.Text>
                </Form.Group>

                <hr className="my-3" />
                <p className="small mb-2">Confirm it is you:</p>

                <Form.Group>
                    <Form.Label className="small">Your password</Form.Label>
                    <div className="d-flex gap-2">
                        <Form.Control
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            disabled={submitting || !reasonOk}
                        />
                        <Button
                            variant="primary"
                            onClick={() => submit({ password })}
                            disabled={submitting || !reasonOk || !password}
                        >
                            {submitting ? <Spinner animation="border" size="sm" /> : "Start"}
                        </Button>
                    </div>
                </Form.Group>

                <div className="text-muted small text-center my-2">or</div>
                <div ref={googleButtonRef} className="d-flex justify-content-center" />
                {!reasonOk && (
                    <div className="text-muted small text-center mt-1">
                        Give a reason first.
                    </div>
                )}

                {error && <Alert variant="danger" className="mt-3 mb-0 small">{error}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImpersonationStartModal;
